import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Upload, FileDown, FileSpreadsheet, CheckCircle, XCircle, FileText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { ProFeatureGate } from '../components/ProFeatureGate';
import { decodeCsv } from '../utils/api';
import { exportToCSV, exportToExcel } from '../utils/export';

const BankPage = () => {
  const [file, setFile] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [detectedBank, setDetectedBank] = useState(null);
  const fileInputRef = useRef(null);
  const { isPro } = useAuth();
  const { t, language } = useLanguage();

  const bankNames = {
    'ing': 'ING Bank',
    'abnamro': 'ABN AMRO',
    'rabobank': 'Rabobank',
    'generic': language === 'nl' ? 'Onbekende bank' : 'Unknown bank'
  };

  const handleFile = async (selectedFile) => {
    const filename = selectedFile?.name?.toLowerCase() || '';
    if (!selectedFile || (!filename.endsWith('.csv') && !filename.endsWith('.txt'))) {
      return;
    }
    
    setFile(selectedFile);
    setLoading(true);
    setDetectedBank(null);
    
    try {
      const data = await decodeCsv(selectedFile, language);
      setResults(data.results);
      setStats({ 
        total: data.found, 
        valid: data.valid, 
        invalid: data.invalid, 
        filename: data.filename 
      });
      setDetectedBank(data.bank_detected || 'generic');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = () => {
    setDragActive(false);
  };

  const content = (
    <div className="py-12 md:py-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="w-16 h-16 rounded-2xl bg-accent/10 flex items-center justify-center mx-auto mb-6">
            <Upload className="w-8 h-8 text-accent" />
          </div>
          <h1 className="font-heading font-bold text-3xl md:text-4xl text-primary dark:text-white mb-4">
            {t('bank_title')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
            {t('bank_subtitle')}
          </p>
        </motion.div>

        {/* Upload area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
            className={`
              relative p-12 rounded-xl border-2 border-dashed cursor-pointer transition-colors
              ${dragActive 
                ? 'border-accent bg-accent/5' 
                : 'border-slate-200 dark:border-slate-700 hover:border-accent hover:bg-slate-50 dark:hover:bg-slate-800/50'}
            `}
            data-testid="file-drop-zone"
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.txt"
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
              className="hidden"
              data-testid="file-input"
            />
            
            <div className="text-center">
              {loading ? (
                <>
                  <div className="w-12 h-12 mx-auto mb-4 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
                  <p className="text-slate-600 dark:text-slate-300">{t('bank_scanning')}</p>
                </>
              ) : file ? (
                <>
                  <div className="w-12 h-12 rounded-xl bg-success/10 flex items-center justify-center mx-auto mb-4">
                    <FileText className="w-6 h-6 text-success" />
                  </div>
                  <p className="font-medium text-slate-900 dark:text-white mb-1">{file.name}</p>
                  <p className="text-sm text-slate-500">
                    {language === 'nl' ? 'Klik om een ander bestand te selecteren' : 'Click to select another file'}
                  </p>
                </>
              ) : (
                <>
                  <div className="w-12 h-12 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-6 h-6 text-slate-400" />
                  </div>
                  <p className="text-slate-600 dark:text-slate-300 mb-2">{t('bank_upload')}</p>
                  <p className="text-sm text-slate-400">{language === 'nl' ? 'CSV of TXT van ING, ABN AMRO, Rabobank' : 'CSV or TXT from ING, ABN AMRO, Rabobank'}</p>
                </>
              )}
            </div>
          </div>
        </motion.div>

        {/* Results */}
        {results && results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Stats */}
            <div className="flex flex-wrap items-center justify-between gap-4 mb-6 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl">
              <div className="flex flex-wrap items-center gap-4 md:gap-6">
                {detectedBank && (
                  <span className="px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium">
                    {bankNames[detectedBank] || detectedBank}
                  </span>
                )}
                <span className="text-sm text-slate-500">
                  <span className="font-semibold text-slate-900 dark:text-white">{stats?.total}</span> {t('bank_found')}
                </span>
                <span className="flex items-center gap-1 text-sm text-success">
                  <CheckCircle className="w-4 h-4" />
                  <span className="font-semibold">{stats?.valid}</span> {t('bulk_valid')}
                </span>
                <span className="flex items-center gap-1 text-sm text-error">
                  <XCircle className="w-4 h-4" />
                  <span className="font-semibold">{stats?.invalid}</span> {t('bulk_invalid')}
                </span>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => exportToCSV(results)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  data-testid="bank-export-csv-btn"
                >
                  <FileDown className="w-4 h-4" />
                  {t('bulk_export_csv')}
                </button>
                <button
                  onClick={() => exportToExcel(results)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  data-testid="bank-export-excel-btn"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  {t('bulk_export_excel')}
                </button>
              </div>
            </div>

            {/* Results table */}
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-slate-50 dark:bg-slate-800/50">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Kenmerk</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('result_tax_type')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('result_year')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">{t('result_period')}</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wide">CSV Rij</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {results.map((r, idx) => (
                      <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-4 py-3">
                          {r.valid ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-success/10 text-success text-xs font-medium">
                              <CheckCircle className="w-3 h-3" />
                              OK
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-error/10 text-error text-xs font-medium">
                              <XCircle className="w-3 h-3" />
                              Error
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm text-slate-900 dark:text-white">
                          {r.kenmerk}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          {r.input_type === 'aanslagnummer' 
                            ? (language === 'nl' ? 'Aanslag' : 'Assessment')
                            : (language === 'nl' ? 'Betaling' : 'Payment')}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.belastingtype?.[language] || r.error || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.jaar || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {r.tijdvak || '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-400">
                          {r.csv_row?.row || '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </motion.div>
        )}

        {results && results.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center p-8 bg-slate-50 dark:bg-slate-800/50 rounded-xl"
          >
            <p className="text-slate-500">
              {language === 'nl' 
                ? 'Geen betalingskenmerken gevonden in dit bestand' 
                : 'No payment references found in this file'}
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );

  if (!isPro()) {
    return (
      <div className="py-12 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ProFeatureGate feature={language === 'nl' ? 'Bank CSV upload voor automatische herkenning' : 'Bank CSV upload for automatic detection'}>
            {content}
          </ProFeatureGate>
        </div>
      </div>
    );
  }

  return content;
};

export default BankPage;
